import { useState } from 'react'
import { useLocale } from '../i18n/LocaleContext'
import { useGallery } from '../hooks/useGallery'
import SectionHeading from './ui/SectionHeading'
import Lightbox from './Lightbox'
import PlaceholderImage from './ui/PlaceholderImage'
import type { GalleryPhoto } from '../types/wp'

// Fallback placeholder photos shown when WordPress is not connected
const fallbackPhotos: GalleryPhoto[] = Array.from({ length: 8 }, (_, i) => ({
  id: i + 1,
  src: '',
  thumbnail: '',
  width: 800,
  height: 600,
}))

// Show this many photos before the gradient fade + "view all" button.
const COLLAPSED_COUNT = 10

export default function PhotoGallery() {
  const { locale, t } = useLocale()
  const { photos: apiPhotos, loading } = useGallery()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)

  const photos = apiPhotos.length > 0 ? apiPhotos : fallbackPhotos

  const collapsed = !showAll && photos.length > COLLAPSED_COUNT
  const visiblePhotos = collapsed ? photos.slice(0, COLLAPSED_COUNT) : photos

  return (
    <section className="section-padding bg-white">
      <div className="container-max">
        <SectionHeading title={t.gallery.title} subtitle={t.gallery.subtitle} />

        {/* Gallery grid */}
        {loading ? (
          <div className="text-center text-text-muted py-12">{t.gallery.loading}</div>
        ) : photos.length === 0 ? (
          <div className="text-center text-text-muted py-12">{t.gallery.empty}</div>
        ) : (
          <div className="relative">
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {visiblePhotos.map((photo, i) => (
                <button
                  key={photo.id}
                  className="block w-full break-inside-avoid overflow-hidden rounded-card group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  onClick={() => setLightboxIndex(i)}
                  aria-label={locale === 'hu' ? `Fotó ${i + 1} megtekintése` : `Vezi foto ${i + 1}`}
                >
                  {photo.thumbnail ? (
                    <img
                      src={photo.thumbnail}
                      alt=""
                      width={photo.width}
                      height={photo.height}
                      className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <PlaceholderImage
                      label={`Photo ${i + 1}`}
                      variant={i % 2 === 0 ? 'nature' : 'camp'}
                      className="w-full h-48"
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Gradient fade into the page background + reveal button */}
            {collapsed && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-56 items-end justify-center bg-gradient-to-t from-white via-white/90 to-transparent">
                <button
                  onClick={() => setShowAll(true)}
                  className="pointer-events-auto mb-2 rounded-btn bg-primary px-6 py-3 text-sm font-medium text-white shadow-card transition-colors hover:bg-primary-hover"
                >
                  {t.gallery.viewAll}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <Lightbox
            photos={photos}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)}
            onNext={() => setLightboxIndex((lightboxIndex + 1) % photos.length)}
          />
        )}
      </div>
    </section>
  )
}
